import { useState, useCallback } from "react";
import { Download, Upload, CheckCircle, XCircle, Loader2, QrCode, Copy, Check, MessageCircle, Mail } from "lucide-react";
import axios from "axios";
import MasterclassUpsell from "@/components/MasterclassUpsell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPanel({ bookingId, totalAmount, onPaymentVerified, customerName, customerPhone, customerEmail }) {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [notifying, setNotifying] = useState(false);

  // Multi-screenshot state
  const [cumulativeVerified, setCumulativeVerified] = useState(0);
  const [paymentsCount, setPaymentsCount] = useState(0);

  // Customer email state (post-payment)
  const [emailInput, setEmailInput] = useState(customerEmail || "");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");

  const notifyTeam = useCallback(async () => {
    setNotifying(true);
    try {
      const res = await axios.post(`${API}/notify-order`, { booking_id: bookingId });
      setWhatsappLink(res.data.whatsapp_link);
    } catch (err) {
      setWhatsappLink(null);
    } finally {
      setNotifying(false);
    }
  }, [bookingId]);

  const fetchPaymentInfo = async () => {
    if (paymentInfo) {
      setShowQR(!showQR);
      return;
    }
    try {
      const res = await axios.get(`${API}/payment-info`);
      setPaymentInfo(res.data);
      setShowQR(true);
    } catch (err) {
      setPaymentInfo(null);
    }
  };

  const copyUPI = () => {
    if (paymentInfo?.upi_id) {
      navigator.clipboard.writeText(paymentInfo.upi_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleScreenshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/upload-payment-screenshot?booking_id=${bookingId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setScanResult(res.data);
      setCumulativeVerified(res.data.cumulative_verified || 0);
      setPaymentsCount(res.data.payments_count || 0);

      if (res.data.fully_paid) {
        setPaymentSuccess(true);
        notifyTeam();
        onPaymentVerified?.(res.data);
      }
    } catch (err) {
      setScanResult({ status: "error", scan_result: { details: "Failed to scan screenshot. Please try again." } });
    } finally {
      setScanning(false);
      // Reset input so user can upload again
      e.target.value = "";
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/generate-order-pdf`, { booking_id: bookingId }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SugarBouquet_Order_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setScanResult({ status: "error", scan_result: { details: "Failed to download PDF. Please try again." } });
    } finally {
      setDownloading(false);
    }
  };

  const handleSendCustomerEmail = async () => {
    if (!emailInput || !emailInput.includes("@")) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setSendingEmail(true);
    setEmailError("");
    try {
      await axios.post(`${API}/send-customer-confirmation`, {
        booking_id: bookingId,
        customer_email: emailInput,
      });
      setEmailSent(true);
    } catch (err) {
      setEmailError("Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const amountDisplay = `₹${totalAmount}`;
  const remainingAmount = totalAmount - cumulativeVerified;

  if (paymentSuccess) {
    return (
      <div className="mt-3 space-y-3" data-testid="payment-panel-success">
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center" data-testid="payment-confirmed-banner">
          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="text-sm font-semibold text-green-800">Payment Confirmed!</p>
          <p className="text-xs text-green-600 mt-1">Order {bookingId} • {amountDisplay}</p>
          {paymentsCount > 1 && (
            <p className="text-xs text-green-500 mt-1">Verified across {paymentsCount} payment(s)</p>
          )}
          {notifying && <p className="text-xs text-gray-500 mt-2">Notifying Sugar Bouquet team...</p>}
        </div>

        {/* Customer Email Input - Ask for email after payment */}
        {!emailSent ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2" data-testid="customer-email-section">
            <p className="text-sm font-medium text-blue-800">Get your order confirmation via email</p>
            <p className="text-xs text-blue-600">We'll send your order summary PDF to your email</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="customer-email-input"
              />
              <button
                onClick={handleSendCustomerEmail}
                disabled={sendingEmail}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                data-testid="send-email-btn"
              >
                {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Send
              </button>
            </div>
            {emailError && <p className="text-xs text-red-600">{emailError}</p>}
          </div>
        ) : (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2" data-testid="email-sent-confirmation">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">Order confirmation sent to <strong>{emailInput}</strong></p>
          </div>
        )}

        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-800 transition-all cursor-pointer disabled:opacity-50"
          data-testid="download-pdf-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download Order Summary (PDF)
        </button>

        {whatsappLink && (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
            data-testid="whatsapp-notify-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Send Order to Sugar Bouquet WhatsApp
          </a>
        )}

        <MasterclassUpsell />
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3" data-testid="payment-panel">
      {/* UPI / QR Payment */}
      <button
        onClick={fetchPaymentInfo}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-black text-white text-sm font-semibold hover:bg-gray-800 transition-all cursor-pointer"
        data-testid="show-qr-btn"
      >
        <QrCode className="w-4 h-4" />
        {showQR ? "Hide" : "Pay"} {amountDisplay} via UPI / QR Code
      </button>

      {showQR && paymentInfo && (
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3 animate-fade-in" data-testid="qr-payment-section">
          <p className="text-sm text-gray-600 text-center font-medium">
            Pay {amountDisplay} via UPI
          </p>
          {totalAmount > 2000 && (
            <p className="text-xs text-amber-700 text-center bg-amber-50 px-2 py-1 rounded-lg">
              UPI apps have a ₹2,000 limit per transaction. You can split into multiple payments and upload each screenshot.
            </p>
          )}
          <div className="flex justify-center">
            <img
              src={paymentInfo.qr_code_url}
              alt="Payment QR Code"
              className="w-48 h-48 object-contain rounded-lg border border-gray-200 bg-white"
              data-testid="payment-qr-code"
            />
          </div>
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm text-gray-700 font-mono bg-white px-3 py-1.5 rounded-lg border border-gray-200">
              {paymentInfo.upi_id}
            </span>
            <button
              onClick={copyUPI}
              className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
              data-testid="copy-upi-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center">
            After payment, upload your screenshot below for instant verification
          </p>
        </div>
      )}

      {/* Payment Progress (for multi-screenshot) */}
      {cumulativeVerified > 0 && !paymentSuccess && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl" data-testid="payment-progress">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs font-medium text-blue-800">Payment Progress</span>
            <span className="text-xs text-blue-600">{paymentsCount} screenshot(s) verified</span>
          </div>
          <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, (cumulativeVerified / totalAmount) * 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-xs text-blue-600">Verified: ₹{cumulativeVerified}</span>
            <span className="text-xs text-blue-800 font-medium">Remaining: ₹{Math.max(0, remainingAmount)}</span>
          </div>
        </div>
      )}

      {/* Upload Screenshot */}
      <label
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all cursor-pointer ${
          scanning
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-wait"
            : "border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
        data-testid="upload-screenshot-btn"
      >
        {scanning ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Scanning payment screenshot...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            {cumulativeVerified > 0 && !paymentSuccess ? "Upload Next Payment Screenshot" : "Upload Payment Screenshot"}
          </>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleScreenshotUpload}
          disabled={scanning}
          className="hidden"
          data-testid="screenshot-file-input"
        />
      </label>

      {/* Scan Result */}
      {scanResult && <ScanResultBanner scanResult={scanResult} />}
    </div>
  );
}

function getScanResultStyles(status) {
  switch (status) {
    case "verified": return "bg-green-50 border-green-200 text-green-800";
    case "partial": return "bg-blue-50 border-blue-200 text-blue-800";
    case "error": return "bg-red-50 border-red-200 text-red-800";
    default: return "bg-amber-50 border-amber-200 text-amber-800";
  }
}

function getScanResultTitle(status, remainingAmount) {
  switch (status) {
    case "verified": return "Payment Fully Verified!";
    case "partial": return `Payment Recorded — ₹${remainingAmount} remaining`;
    case "error": return "Scan Failed";
    default: return "Could Not Detect Amount";
  }
}

function ScanResultIcon({ status }) {
  if (status === "verified") return <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />;
  if (status === "partial") return <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />;
  return <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />;
}

function ScanResultBanner({ scanResult }) {
  const description = scanResult.status === "partial"
    ? "Please upload the next payment screenshot to complete verification."
    : scanResult.scan_result?.details || "Please try uploading a clearer screenshot.";

  return (
    <div
      className={`p-3 rounded-xl border text-sm ${getScanResultStyles(scanResult.status)}`}
      data-testid="scan-result"
    >
      <div className="flex items-start gap-2">
        <ScanResultIcon status={scanResult.status} />
        <div>
          <p className="font-medium">{getScanResultTitle(scanResult.status, scanResult.remaining_amount)}</p>
          <p className="text-xs mt-1 opacity-80">{description}</p>
        </div>
      </div>
    </div>
  );
}
